import React, { useState } from "react";
import { Instagram, Youtube, Linkedin } from "lucide-react";
import { TbBrandUpwork, TbBrandFiverr } from "react-icons/tb";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { subscribeNewsletter } from "../api";

const SOCIAL_ICONS = {
  Instagram: Instagram,
  YouTube: Youtube,
  LinkedIn: Linkedin,
  Upwork: TbBrandUpwork,
  Fiverr: TbBrandFiverr,
};

export { SOCIAL_ICONS };

export default function Footer() {
  const { content } = useContent();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setSubmitting(true);
    try {
      await subscribeNewsletter({ firstName, lastName, email: email.trim() });
      toast.success("You're in the loop! Thanks for subscribing.");
      setFirstName("");
      setLastName("");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0f0f0f] texture-dark text-white" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="border-t border-white/60 pt-8 flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <h3 className="font-comfortaa text-xl md:text-2xl text-white flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-white inline-block" />
            {content.footer.newsletterTitle}
          </h3>
          <div className="flex items-center gap-5" data-testid="footer-socials">
            {content.socials.map((s) => {
              const Icon = SOCIAL_ICONS[s.name] || Instagram;
              return (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="text-white hover:opacity-50"
                  style={{ transition: "opacity 0.25s ease" }}
                >
                  <Icon size={20} strokeWidth={1.5} />
                </a>
              );
            })}
          </div>
        </div>

        <p className="font-comfortaa text-sm text-white/80 max-w-lg mt-10 leading-relaxed">
          {content.footer.newsletterText}
        </p>

        <form onSubmit={handleSubscribe} className="mt-8" data-testid="newsletter-form">
          <div className="flex flex-col sm:flex-row gap-3 max-w-lg">
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="newsletter-firstname"
              className="bg-white text-neutral-900 placeholder:text-neutral-400 font-comfortaa text-sm px-5 py-4 w-full sm:w-1/2 outline-none focus:ring-2 focus:ring-white/40"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              data-testid="newsletter-lastname"
              className="bg-white text-neutral-900 placeholder:text-neutral-400 font-comfortaa text-sm px-5 py-4 w-full sm:w-1/2 outline-none focus:ring-2 focus:ring-white/40"
            />
          </div>
          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="newsletter-email"
            className="bg-white text-neutral-900 placeholder:text-neutral-400 font-comfortaa text-sm px-5 py-4 w-full max-w-[calc(theme(maxWidth.lg)*0.55)] sm:w-[55%] mt-3 outline-none focus:ring-2 focus:ring-white/40 block"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="newsletter-submit"
            className="pill-button mt-8 disabled:opacity-50"
          >
            {content.footer.newsletterButton}
          </button>
        </form>

        {/* Availability badge */}
        <div className="flex justify-center mt-14">
          <span
            className="inline-flex items-center gap-2.5 border border-white/20 rounded-full px-4 py-2"
            data-testid="availability-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-[#8fae4f] opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#8fae4f]" />
            </span>
            <span className="font-comfortaa text-xs text-white/80">Available for new projects</span>
          </span>
        </div>

        <div className="border-t border-white/60 mt-14 pt-6">
          <p className="font-comfortaa text-xs text-white/70 text-center" data-testid="footer-copyright">
            {content.brand.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
